import type { Session, User } from "@supabase/supabase-js";
import type { UserData } from "@/types/user-data";
import type { UserProfile } from "@/types/storage";

type BypassPersona = {
  email: "teacher.test@aieducators.ai" | "district.test@aieducators.ai";
  workspacePersona: "teacher" | "district_leader";
  schoolId: string | null;
};

const TEST_PERSONAS: Record<BypassPersona["email"], BypassPersona> = {
  "teacher.test@aieducators.ai": {
    email: "teacher.test@aieducators.ai",
    workspacePersona: "teacher",
    schoolId: "test-school-001",
  },
  "district.test@aieducators.ai": {
    email: "district.test@aieducators.ai",
    workspacePersona: "district_leader",
    schoolId: null,
  },
};

const DEFAULT_PERSONA = TEST_PERSONAS["teacher.test@aieducators.ai"];

function getConfiguredPersona(): BypassPersona {
  const configuredEmail = import.meta.env.VITE_BYPASS_USER_EMAIL?.trim();
  return TEST_PERSONAS[configuredEmail as BypassPersona["email"]] ?? DEFAULT_PERSONA;
}

function createDeterministicUserId(email: string): string {
  let hash = 0;
  for (const character of email) {
    hash = (hash * 31 + character.charCodeAt(0)) | 0;
  }
  return `local-test-${Math.abs(hash).toString(16)}`;
}

export function isAuthBypassEnabled(): boolean {
  return import.meta.env.DEV && import.meta.env.VITE_AUTH_BYPASS_ENABLED === "true";
}

function createMockUser(persona: BypassPersona): User {
  const now = new Date().toISOString();
  const id = createDeterministicUserId(persona.email);

  return {
    id,
    aud: "authenticated",
    role: "authenticated",
    email: persona.email,
    email_confirmed_at: now,
    phone: "",
    confirmation_sent_at: now,
    last_sign_in_at: now,
    app_metadata: { provider: "email", providers: ["email"] },
    user_metadata: { email: persona.email, email_verified: true },
    identities: [],
    created_at: now,
    updated_at: now,
    is_anonymous: false,
  };
}

function createMockSession(user: User): Session {
  const expiresAt = Math.floor(Date.now() / 1000) + 60 * 60;
  return {
    access_token: `local-test-access-${user.id}`,
    refresh_token: `local-test-refresh-${user.id}`,
    expires_in: 60 * 60,
    expires_at: expiresAt,
    token_type: "bearer",
    user,
  };
}

function createMockUserData(persona: BypassPersona, user: User): UserData {
  return {
    user_id: user.id,
    created_at: user.created_at,
    email: persona.email,
    user_name: persona.workspacePersona === "teacher" ? "Test Teacher" : "Test District Leader",
    school_name: persona.workspacePersona === "teacher" ? "Test School" : undefined,
    workspace_persona: persona.workspacePersona,
    onboarding: {},
    survey: {},
    survey_completed_at: user.created_at,
  };
}

function createMockProfile(persona: BypassPersona, user: User): UserProfile {
  return {
    id: user.id,
    school_id: persona.schoolId,
    district_id: "test-district-001",
    is_pro: false,
    is_super_admin: false,
    user_status: "active",
  };
}

export type AuthBypassState = {
  session: Session;
  userData: UserData;
  profile: UserProfile;
};

export function getAuthBypassState(): AuthBypassState | null {
  if (!isAuthBypassEnabled()) return null;

  const persona = getConfiguredPersona();
  const user = createMockUser(persona);
  return {
    session: createMockSession(user),
    userData: createMockUserData(persona, user),
    profile: createMockProfile(persona, user),
  };
}
